import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import {
  ACCEPTANCE_LABEL_KEY,
  ACCEPTANCE_TONE,
  CHANNEL_LABEL_KEY,
  DELIVERY_LABEL_KEY,
  DELIVERY_TONE,
} from '@/lib/notifications/labels';
import type {
  NotificationAcceptanceState,
  NotificationChannel,
  NotificationDeliveryState,
} from '@/lib/notifications/types';

// Per-PNR-group notification + acceptance status, as two friendly-label badges
// (delivery telemetry + acceptance-gate state) with an optional channel tag.
// Friendly labels primary, tones ride the white-label Badge tokens (§18).
// Copy is the same `pnr` bundle the per-passenger row renders, reached through
// the shared enum → key tables in lib/notifications/labels.

interface NotificationStatusBadgesProps {
  deliveryState: NotificationDeliveryState;
  acceptanceState: NotificationAcceptanceState;
  channel: NotificationChannel | null;
}

export function NotificationStatusBadges({
  deliveryState,
  acceptanceState,
  channel,
}: NotificationStatusBadgesProps) {
  const { t } = useTranslation('pnr');
  const acceptanceLabel = t(ACCEPTANCE_LABEL_KEY[acceptanceState]);
  const deliveryLabel = t(DELIVERY_LABEL_KEY[deliveryState]);
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid="notification-status">
      <Badge
        variant={ACCEPTANCE_TONE[acceptanceState]}
        size="sm"
        data-testid="acceptance-state"
        title={t('pax.acceptanceTitle', { state: acceptanceLabel })}
      >
        {acceptanceLabel}
      </Badge>
      <Badge
        variant={DELIVERY_TONE[deliveryState]}
        size="sm"
        data-testid="delivery-state"
        title={t('pax.deliveryTitle', { state: deliveryLabel })}
      >
        {deliveryLabel}
        {channel ? (
          <span className="opacity-70"> · {t(CHANNEL_LABEL_KEY[channel])}</span>
        ) : null}
      </Badge>
    </div>
  );
}
